// Single source of truth for Hair Art Clinics NAP data.
// Used to generate LocalBusiness / MedicalClinic / Organization JSON-LD
// for every language, so schema stays consistent across the site.

export const CLINIC_BRAND = 'Hair Art Clinics';
export const CLINIC_SITE_URL = 'https://hairartclinics.net';
export const CLINIC_LOGO_URL = `${CLINIC_SITE_URL}/logo.png`;
export const CLINIC_OG_IMAGE = `${CLINIC_SITE_URL}/og-image.jpg`;

export const CLINIC_EMAIL = 'siostarr@hairartclinics.com';
export const CLINIC_PRIMARY_PHONE = '+31638523131';
export const CLINIC_PRIMARY_PHONE_HUMAN = '+31 6 38523131';

/** Opening hours confirmed by the clinic: every day 10:00–18:00. */
export const CLINIC_OPENING_DAYS: readonly string[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;
export const CLINIC_OPENS = '10:00';
export const CLINIC_CLOSES = '18:00';
/** Schema.org / OpenGraph short form. */
export const CLINIC_OPENING_HOURS = `Mo-Su ${CLINIC_OPENS}-${CLINIC_CLOSES}`;

export const CLINIC_OPENING_HOURS_SPECIFICATION = {
  '@type': 'OpeningHoursSpecification',
  dayOfWeek: [...CLINIC_OPENING_DAYS],
  opens: CLINIC_OPENS,
  closes: CLINIC_CLOSES,
};

// Official social / external profiles. Single source of truth for
// schema.org `sameAs` across every page + language and for the
// footer social icons. Add or remove here only.
export const CLINIC_SAME_AS: readonly string[] = [
  'https://www.facebook.com/hairartclinics',
  'https://www.instagram.com/hairartclinics',
  'https://www.youtube.com/@hairartclinics',
  'https://www.linkedin.com/company/hairartclinics',
] as const;

export type ClinicLocation = {
  id: string;
  name: string;
  streetAddress: string;
  addressLocality: string;
  postalCode?: string;
  addressCountry: string; // ISO 3166-1 alpha-2
  telephone: string;
  geo?: { latitude: number; longitude: number };
  areaServed?: string[];
};

export const CLINIC_LOCATIONS: ClinicLocation[] = [
  {
    id: 'nl-den-haag',
    name: 'Hair Art Clinics — Rijswijk',
    streetAddress: 'Verrijn Stuartlaan 17',
    addressLocality: 'Rijswijk',
    postalCode: '2288 EK',
    addressCountry: 'NL',
    telephone: '+31638523131',
    geo: { latitude: 52.0402709, longitude: 4.3341944 },
    areaServed: ['NL', 'BE'],
  },
];


const DESCRIPTIONS: Record<string, string> = {
  en: 'Hair Art Clinics offers hair restoration and aesthetic hair treatments in Den Haag, the Netherlands.',
  nl: 'Hair Art Clinics biedt haarherstel en esthetische haarbehandelingen in Den Haag, Nederland.',
  fr: 'Hair Art Clinics propose la restauration capillaire et des traitements capillaires esthétiques à La Haye, aux Pays-Bas.',
  tr: 'Hair Art Clinics; Hollanda Den Haag’da saç restorasyonu ve estetik saç uygulamaları sunar.',
  ar: 'تقدم Hair Art Clinics خدمات استعادة الشعر والعلاجات الجمالية للشعر في لاهاي، هولندا.',
};

const buildLocationNode = (loc: ClinicLocation) => ({
  '@type': 'LocalBusiness',
  '@id': `${CLINIC_SITE_URL}/#location-${loc.id}`,
  name: loc.name,
  telephone: loc.telephone,
  email: CLINIC_EMAIL,
  url: CLINIC_SITE_URL,
  hasMap: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${loc.streetAddress}, ${loc.postalCode ?? ''} ${loc.addressLocality}`.replace(/\s+/g, ' ').trim(),
  )}`,
  address: {
    '@type': 'PostalAddress',
    streetAddress: loc.streetAddress,
    addressLocality: loc.addressLocality,
    ...(loc.postalCode ? { postalCode: loc.postalCode } : {}),
    addressCountry: loc.addressCountry,
  },
  openingHours: CLINIC_OPENING_HOURS,
  openingHoursSpecification: [CLINIC_OPENING_HOURS_SPECIFICATION],
  ...(loc.geo
    ? { geo: { '@type': 'GeoCoordinates', ...loc.geo } }
    : {}),
  ...(loc.areaServed && loc.areaServed.length
    ? { areaServed: loc.areaServed }
    : {}),
  parentOrganization: { '@id': `${CLINIC_SITE_URL}/#organization` },
});

/**
 * Builds the canonical Organization + per-location MedicalClinic graph
 * for the given language. Rendered as a single JSON-LD @graph block by
 * SEOHead so every page across every language shares one source.
 */
export const buildClinicOrganizationGraph = (language: string) => {
  const description = DESCRIPTIONS[language] ?? DESCRIPTIONS.en;
  const org = {
    '@type': ['Organization'],
    '@id': `${CLINIC_SITE_URL}/#organization`,
    name: CLINIC_BRAND,
    url: CLINIC_SITE_URL,
    logo: CLINIC_LOGO_URL,
    image: CLINIC_OG_IMAGE,
    email: CLINIC_EMAIL,
    telephone: CLINIC_PRIMARY_PHONE,
    areaServed: ['NL', 'BE'],
    sameAs: [...CLINIC_SAME_AS],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: CLINIC_PRIMARY_PHONE,
        contactType: 'customer service',
        email: CLINIC_EMAIL,
        areaServed: ['NL', 'BE'],
        availableLanguage: ['nl', 'en', 'fr', 'tr', 'ar'],
      },
    ],
    location: CLINIC_LOCATIONS.map((l) => ({ '@id': `${CLINIC_SITE_URL}/#location-${l.id}` })),
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [org, ...CLINIC_LOCATIONS.map(buildLocationNode)],
  };
};
